import type { TFunction } from 'i18next';

/**
 * Why a delivery run failed, in words — the run's `errorCode` mapped to a title
 * and a next step, with the stored `errorMessage` as the specific reason.
 *
 * SFTP runs store short codes (`ENRICHMENT_NOT_FOUND`) while GMC-file runs store
 * the raw exception type (`EnrichmentNotFoundError`), so both spellings map to
 * the same copy. Runs from before the backend's error-unwrapping fix carry
 * `UNHANDLED_ERROR` / "Activity task failed", which says nothing — treated as
 * "no details recorded".
 */

/** Copy keys under `deliveries:runError.codes`. */
type ErrorCopyKey =
  | 'enrichmentNotFound'
  | 'emptyFeed'
  | 'sftpCredentials'
  | 'sftpPermission'
  | 'sftpUnreachable'
  | 'sftpVerification'
  | 'invalidSettings'
  | 'unknownField'
  | 'gmcFileNameMismatch'
  | 'gmcRejected'
  | 'unhandled';

const CODE_TO_COPY: Record<string, ErrorCopyKey> = {
  ENRICHMENT_NOT_FOUND: 'enrichmentNotFound',
  EnrichmentNotFoundError: 'enrichmentNotFound',
  EMPTY_FEED: 'emptyFeed',
  EmptyFeedError: 'emptyFeed',
  SFTP_CREDENTIALS: 'sftpCredentials',
  SFTPCredentialsError: 'sftpCredentials',
  SFTP_PERMISSION: 'sftpPermission',
  SFTPPermissionError: 'sftpPermission',
  SFTPConnectionError: 'sftpUnreachable',
  SFTPUploadError: 'sftpUnreachable',
  SFTP_VERIFICATION: 'sftpVerification',
  VALIDATION: 'invalidSettings',
  CONFIG: 'invalidSettings',
  ValidationError: 'invalidSettings',
  ConfigurationError: 'invalidSettings',
  UnknownFieldError: 'unknownField',
  SftpFileNameNoMatchingDatasource: 'gmcFileNameMismatch',
  GmcProcessingFailedError: 'gmcRejected',
  FailureThresholdExceededError: 'gmcRejected',
  UNHANDLED_ERROR: 'unhandled',
};

/** The catch-all message old runs stored — carries no information. */
const UNINFORMATIVE_MESSAGES = new Set(['activity task failed']);

/** Readable run failure. `detail` is null when nothing useful was recorded. */
export interface DeliveryErrorDescription {
  title: string;
  /** The stored message — the specific reason. */
  detail: string | null;
  /** What to do about it; null when there's nothing general to suggest. */
  nextStep: string | null;
  /** The raw code, for support. */
  code: string;
}

/**
 * Title-case an unmapped code of either spelling:
 * `SOME_NEW_CODE` → "Some New Code", `SomeNewError` → "Some New Error".
 */
export function humanizeErrorCode(code: string): string {
  return code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Map a run's `errorCode` + `errorMessage` to what the UI should say. */
export function describeDeliveryError(
  code: string,
  message: string | null | undefined,
  t: TFunction<'deliveries'>,
): DeliveryErrorDescription {
  const trimmed = message?.trim() ?? '';
  const informative = trimmed !== '' && !UNINFORMATIVE_MESSAGES.has(trimmed.toLowerCase());
  const key = CODE_TO_COPY[code];

  if (key === 'unhandled' && !informative) {
    return {
      title: t('runError.codes.unhandled.title'),
      detail: null,
      nextStep: t('runError.noDetails'),
      code,
    };
  }
  if (!key) {
    return {
      title: humanizeErrorCode(code),
      detail: informative ? trimmed : null,
      nextStep: informative ? null : t('runError.noDetails'),
      code,
    };
  }
  const nextStep = t(`runError.codes.${key}.nextStep`);
  return {
    title: t(`runError.codes.${key}.title`),
    detail: informative ? trimmed : null,
    nextStep: nextStep === '' ? null : nextStep,
    code,
  };
}
